import classNames from "classnames";
import React from "react";

import { defaults } from "../apps/configurator/defaults";
import { setOptionValue } from "../apps/configurator/reducers";
import {
    productAttributeOptionGroupSelector,
    rootProductSelector,
} from "../apps/configurator/selectors";
import { useAppDispatch, useAppSelector } from "../apps/reducers";
import {
    buildRelevantLevelVariants,
    findLevelVariants,
    getProductVariantsForOptionCodes,
} from "../apps/selectors";
import { ConfiguratorTypes, OptionTypes } from "../constants";
import {
    IOptionCode,
    IOptionSingleValue,
    IProduct,
    IProductAttributeOptionGroup,
    InternalProduct,
} from "../models/catalogue";
import { IImageURL } from "../models/nominals";
import { PriceType, getProductPrice } from "../utils/catalogue";
import { roundedMoney } from "../utils/format";
import { listPossibleOptionValues } from "../utils/sorting";
import { Image } from "./Image";

import styles from "./ProductOptionRadioSelector.module.scss";

interface IProps {
    configuratorType?: ConfiguratorTypes;
    selectorID: string;
    rootProduct: IProduct;
    code: IOptionCode;
    value?: IOptionSingleValue;
    options: IOptionSingleValue[];
    prefixNameCapped: string;
    configuratorClass: string;
    prefixClass: string;
    onChange: (event: React.FormEvent<HTMLInputElement>) => void;
    onKeyboardDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onClick: () => void;
    isIcon?: boolean;
    showPricing?: boolean;
    isPlpPanelStyle?: boolean;
}

function buildPricingOverlay(
    variant: IProduct | undefined,
    showPricing: boolean | undefined,
): React.ReactNode {
    if (!showPricing || !variant || !variant.availability.is_available_to_buy) {
        return null;
    }
    const retailPrice = getProductPrice(variant.price, {
        priceType: PriceType.RETAIL,
        includePostDiscountAddons: true,
        quantity: 1,
    });
    const cosmeticPrice = getProductPrice(variant.price, {
        priceType: PriceType.COSMETIC_EXCL_TAX,
        includePostDiscountAddons: true,
        quantity: 1,
    });
    if (cosmeticPrice.lessThan(retailPrice)) {
        return (
            <div className={styles.pricingOverlay}>
                <span className={styles.actualPrice}>
                    <span className="ada-screenreader-only">
                        Current Price:
                    </span>
                    {roundedMoney(cosmeticPrice)}
                </span>
                <span className={styles.retailPrice}>
                    <span className="ada-screenreader-only">
                        Original Price:
                    </span>
                    {roundedMoney(retailPrice)}
                </span>
            </div>
        );
    }
    return (
        <div className={styles.pricingOverlay}>
            <span>{roundedMoney(retailPrice)}</span>
        </div>
    );
}

function buildLabelContent(
    iconImageURL: IImageURL | null,
    isPanel: boolean,
    option: string,
    isPlpPanelStyle: boolean | undefined,
): React.ReactNode {
    const content = (
        <>
            {iconImageURL && isPanel && (
                <Image className={styles.icon} src={iconImageURL} alt="" />
            )}
            {option}
        </>
    );
    return isPlpPanelStyle ? <div>{content}</div> : content;
}

function getCodeVariants(
    options: IOptionSingleValue[],
    rootProduct: IProduct,
    code: IOptionCode,
): Record<IOptionSingleValue, InternalProduct[]> {
    return options.reduce(
        (variants: Record<IOptionSingleValue, InternalProduct[]>, option) => {
            variants[option] = getProductVariantsForOptionCodes(
                [rootProduct],
                [code],
                { [code]: option },
            );
            return variants;
        },
        {},
    );
}

function getLabelConfig(
    rootProduct: IProduct,
    code: IOptionCode,
    prefixNameCapped: string,
    options: IOptionSingleValue[],
): Array<{
    label: string;
    values: IOptionSingleValue[];
    description?: string;
}> {
    const labelConfigReadonly =
        rootProduct.attributes.product_option_labels?.value[code] || [];
    const labelConfig = labelConfigReadonly.map((item) => ({
        label: item.label,
        values: [...item.values],
        description: item.description,
    }));
    if (labelConfig.length <= 0) {
        labelConfig.push({
            label: prefixNameCapped,
            values: options,
            description: undefined,
        });
    }
    return labelConfig;
}

function isThreeColumnDesign(code: IOptionCode): boolean {
    switch (code) {
        case OptionTypes.SIZE:
        case OptionTypes.COLOR:
        case OptionTypes.PROFILE:
        case OptionTypes.HEIGHT:
            return true;
        default:
            return false;
    }
}

function findIconImageURL(
    productAttributeOptionGroup: IProductAttributeOptionGroup | null,
    option: IOptionSingleValue,
): IImageURL | null {
    let iconImageURL = null;
    productAttributeOptionGroup?.options.forEach((o) => {
        if (o.option === option) {
            iconImageURL = o.icon_image_url;
        }
    });
    return iconImageURL;
}

export const ProductOptionRadioSelector = (props: IProps): React.ReactNode => {
    const state = useAppSelector(
        (rootState) => rootState.configurator || defaults,
    );
    const dispatch = useAppDispatch();

    const rootProductFromState = rootProductSelector(state);
    const productAttributeOptionGroup = productAttributeOptionGroupSelector(
        state,
        props.code,
    );
    const namespace = rootProductFromState
        ? rootProductFromState.product_class_slug
        : null;
    const optionValues = state.ui.optionValues;

    // Refs #21452 - zlebar
    // Overriding `props.onChange` to accommodate
    // tsi-sealy's Desired "Level" behavior
    const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
        e.preventDefault();

        if ("option_level" in optionValues) {
            // Build a more updated optionValues object
            const updatedOptionValues = {
                ...optionValues,
                [props.code]: e.currentTarget.value,
            };

            // Build our Level options
            const levelOptions = listPossibleOptionValues(
                "option_level",
                props.rootProduct,
            );
            const levelVariants = buildRelevantLevelVariants(
                levelOptions,
                updatedOptionValues,
                props.rootProduct,
                "option_level",
            );
            const levelColumns =
                levelVariants.length > 2
                    ? findLevelVariants(levelVariants, updatedOptionValues)
                    : levelVariants;

            // If the newly selected options doesn't come in the currently selected Level
            if (optionValues.option_level !== levelColumns[0].level) {
                if (namespace) {
                    // Change the Level
                    dispatch(
                        setOptionValue({
                            namespace,
                            code: "option_level",
                            index: 0,
                            totalNumValues: 1,
                            value: levelColumns[0].level,
                        }),
                    );
                }
            }
        }

        props.onChange(e);
    };

    const codeVariants = getCodeVariants(
        props.options,
        props.rootProduct,
        props.code,
    );

    const labelConfig = getLabelConfig(
        props.rootProduct,
        props.code,
        props.prefixNameCapped,
        props.options,
    );

    // Render each label group
    const groups = labelConfig.map((labelGroup, groupIndex) => {
        // Are we rendering within a Panel?
        const isPanel = props.configuratorType === ConfiguratorTypes.PANEL;
        const threeColumn = isThreeColumnDesign(props.code);

        const optionBtns = labelGroup.values.map((option, index) => {
            // Make sure option is valid
            if (props.options.indexOf(option) === -1) {
                return null;
            }
            const optionContainerClass = classNames({
                "configurator__radio-option-container": true,
                [`configurator__radio-option-container--${props.selectorID}`]: true,
                [styles.twoColumnContainer]:
                    props.isPlpPanelStyle || (isPanel && !threeColumn),
                [styles.threeColumnContainer]:
                    !props.isPlpPanelStyle && isPanel && threeColumn,
            });
            const optionClass = classNames({
                "configurator__radio-option": true,
                [`configurator__radio-option--${props.selectorID}`]: true,
                [styles.vertical]:
                    isPanel &&
                    (props.code === OptionTypes.SIZE ||
                        props.code === OptionTypes.COLOR ||
                        props.code === OptionTypes.HEIGHT ||
                        props.code === OptionTypes.PROFILE),
                [styles.activeOption]: option === props.value,
                [styles.panelRadioOption]: isPanel,
                [styles.alternativePanelRadioOption]: props.isPlpPanelStyle,
            });
            const optionNameSuffix = `${props.rootProduct.id}-${props.selectorID}-${props.code}-${groupIndex}-${index}`;
            const optionName = `configurator__radio-option--${optionNameSuffix}`;
            const optionsGroupName = `${props.code}-${props.selectorID}`;
            const optionSelected = option === props.value;

            const iconImageURL = findIconImageURL(
                productAttributeOptionGroup,
                option,
            );

            return (
                <li key={option} className={optionContainerClass}>
                    <input
                        type="radio"
                        id={optionName}
                        name={optionsGroupName}
                        onChange={handleChange}
                        onClick={props.onClick}
                        onKeyDown={props.onKeyboardDown}
                        value={option}
                        checked={optionSelected}
                    />
                    <label
                        htmlFor={optionName}
                        className={optionClass}
                        aria-selected={optionSelected}
                        tabIndex={0}
                    >
                        {buildLabelContent(
                            iconImageURL,
                            isPanel,
                            option,
                            props.isPlpPanelStyle,
                        )}
                        {codeVariants?.[option] &&
                            buildPricingOverlay(
                                codeVariants[option][0],
                                props.showPricing,
                            )}
                    </label>
                </li>
            );
        });

        const prefixClass = classNames({
            [props.prefixClass]: true,
            "ada-screenreader-only": labelConfig.length <= 1 && isPanel,
        });

        return (
            <fieldset key={labelGroup.label}>
                <legend className={prefixClass}>{labelGroup.label}</legend>
                {labelGroup.description && (
                    <p className={styles.description}>
                        {labelGroup.description}
                    </p>
                )}
                <ul className={props.configuratorClass}>{optionBtns}</ul>
            </fieldset>
        );
    });

    return (
        <div key={props.code} className="configurator__radio-container">
            {groups}
        </div>
    );
};
